import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import debounce from 'lodash/debounce';

import {
  UserFilters,
  UserFiltersComponent,
} from '../../../shared/components/user-filters/user-filters.component';
import {
  UserTableRow,
  UserTableAction,
  UserTableConfig,
} from '../../../shared/components/users-table/users-table.types';
import { UsersTableComponent } from '../../../shared/components/users-table/users-table.component';
import {
  UserService,
  type AuthenticatedUser,
} from '../../../shared/services/user.service';
import { SelectOption } from '../../../shared/types/select-option';
import { UserRole, UserStatus } from '#backend/common/user-types';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';
import type { UserResponseDto } from '#backend/entity/loaded/crm/User';

const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ADMIN_GEOAMBIENTE]: 'Administrador Geoambiente',
  [UserRole.OPERADOR_GEOAMBIENTE]: 'Operador Geoambiente',
  [UserRole.ADMIN_CLIENTE]: 'Administrador',
  [UserRole.VISUALIZADOR_CLIENTE]: 'Visualizador',
};

const GEOAMBIENTE_ROLE_SET: readonly UserRole[] = [
  UserRole.ADMIN_GEOAMBIENTE,
  UserRole.OPERADOR_GEOAMBIENTE,
];

const CLIENT_AVAILABLE_ROLES: readonly UserRole[] = [
  UserRole.ADMIN_CLIENTE,
  UserRole.VISUALIZADOR_CLIENTE,
];

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [MatSnackBarModule, UsersTableComponent, UserFiltersComponent],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit {
  private router = inject(Router);
  private snackBar = inject(SnackbarService);
  private orpc = inject(SmartOrpcClientService).safeClient;
  private userService = inject(UserService);

  dataSource = new MatTableDataSource<UserTableRow>([]);
  isLoading = false;
  totalItems = 0;

  filters: UserFilters = {
    searchText: '',
    role: '',
    status: '',
    organizationId: null,
  };

  pageIndex = 0;
  pageSize = 10;
  sortActive: string | null = null;
  sortDirection: Sort['direction'] = '';

  roles: SelectOption<UserRole>[] = [];
  statuses: SelectOption<UserStatus>[] = [
    { value: UserStatus.ACTIVE, label: 'Ativo' },
    { value: UserStatus.INACTIVE, label: 'Inativo' },
  ];
  companies: SelectOption<number>[] = [];
  showCompanyFilter = true;
  authenticatedUser: AuthenticatedUser | null = null;
  isGeoambiente = false;
  canManageUsers = false;

  tableConfig: UserTableConfig = {
    columns: [
      'name',
      'email',
      'role',
      'organizationName',
      'lastAccess',
      'status',
    ],
    showActionsColumn: true,
  };

  tableActions: UserTableAction[] = [
    {
      label: 'Visualizar',
      icon: 'visibility',
      action: (user: UserTableRow) => this.viewUser(user),
    },
    {
      label: 'Editar',
      icon: 'edit',
      action: (user: UserTableRow) => this.editUser(user),
      condition: (user: UserTableRow) => this.canEditUser(user),
    },
    {
      label: 'Desativar',
      icon: 'block',
      action: (user: UserTableRow) => this.toggleUserStatus(user),
      condition: (user: UserTableRow) =>
        this.canEditUser(user) && user.status === UserStatus.ACTIVE,
    },
    {
      label: 'Ativar',
      icon: 'check_circle',
      action: (user: UserTableRow) => this.toggleUserStatus(user),
      condition: (user: UserTableRow) =>
        this.canEditUser(user) && user.status === UserStatus.INACTIVE,
    },
  ];

  private handleSearchDebounced = debounce(() => {
    void this.loadUsers({ resetPage: true });
  }, 300);

  async ngOnInit(): Promise<void> {
    await this.initialize();
  }

  onSearchChange(searchText: string): void {
    this.filters.searchText = searchText;
    this.handleSearchDebounced();
  }

  onRoleChange(role: UserRole | ''): void {
    this.filters.role = role;
    void this.loadUsers({ resetPage: true });
  }

  onStatusChange(status: UserStatus | ''): void {
    this.filters.status = status;
    void this.loadUsers({ resetPage: true });
  }

  onCompanyChange(organizationId: number | null): void {
    this.filters.organizationId = organizationId;
    void this.loadUsers({ resetPage: true });
  }

  onClearFilters(): void {
    this.filters = {
      searchText: '',
      role: '',
      status: '',
      organizationId: null,
    };
    this.sortActive = null;
    this.sortDirection = '';
    void this.loadUsers({ resetPage: true });
  }

  onCreateUser(): void {
    this.router.navigate(['/admin/users/create']);
  }

  onSortChange(sort: Sort): void {
    if (!sort.direction) {
      this.sortActive = null;
      this.sortDirection = '';
    } else {
      this.sortActive = sort.active;
      this.sortDirection = sort.direction;
    }

    void this.loadUsers();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    void this.loadUsers();
  }

  viewUser(user: UserTableRow): void {
    void this.router.navigate(['/admin/users', user.id]);
  }

  editUser(user: UserTableRow): void {
    void this.router.navigate(['/admin/users', user.id, 'edit']);
  }

  async toggleUserStatus(user: UserTableRow): Promise<void> {
    if (!this.canEditUser(user)) {
      this.snackBar.alert('Você não tem permissão para alterar este usuário.');
      return;
    }

    const {
      data: updated,
      error,
      isDefined,
    } = await this.orpc.users.toggleStatus({
      id: user.id,
    });
    if (error) {
      console.error('[Users] Falha ao alternar status do usuário', error);
      this.snackBar.alert(
        isDefined
          ? error.message
          : 'Não foi possível alterar o status do usuário.',
      );
    }
    if (updated) {
      const action =
        updated.status === UserStatus.ACTIVE ? 'ativado' : 'desativado';
      this.snackBar.alert(`Usuário ${action} com sucesso!`);
      this.replaceUserInTable(updated);
    }
  }

  private async initialize(): Promise<void> {
    this.isLoading = true;
    try {
      const authUser = await this.userService.requireUser();
      this.authenticatedUser = authUser;
      this.isGeoambiente = GEOAMBIENTE_ROLE_SET.includes(authUser.role);
      this.canManageUsers =
        this.isGeoambiente || authUser.role === UserRole.ADMIN_CLIENTE;
      this.showCompanyFilter = this.isGeoambiente;
      this.roles = this.buildRoleOptions(authUser.role);

      if (!this.isGeoambiente) {
        this.filters.organizationId = authUser.organizationId ?? null;
      }

      await this.loadCompanies();
      await this.loadUsers({ resetPage: true, skipLoadingFlag: true });
    } catch (error) {
      console.error('[Users] Falha ao inicializar tela de usuários', error);
      this.snackBar.alert('Não foi possível carregar as informações iniciais.');
    } finally {
      this.isLoading = false;
    }
  }

  private async loadCompanies(): Promise<void> {
    if (!this.isGeoambiente) {
      const organizationId = this.authenticatedUser?.organizationId ?? null;
      const organizationName =
        this.authenticatedUser?.organizationName ?? 'Minha organização';

      this.companies = organizationId
        ? [
            {
              value: organizationId,
              label: organizationName,
            },
          ]
        : [];

      return;
    }

    const {
      data: resp,
      error,
      isDefined,
    } = await this.orpc.organization.list({});

    if (error) {
      console.error('[Users] Falha ao carregar empresas', error);
      this.companies = [];
      this.snackBar.alert('Não foi possível carregar a lista de empresas.');
    } else {
      this.companies = resp.items.map((o) => ({
        value: o.id!,
        label: o.name,
      }));
    }
  }

  private async loadUsers(options?: {
    resetPage?: boolean;
    skipLoadingFlag?: boolean;
  }): Promise<void> {
    if (options?.resetPage) {
      this.pageIndex = 0;
    }

    const resolvedOrganizationId = this.isGeoambiente
      ? this.filters.organizationId
      : (this.authenticatedUser?.organizationId ?? null);

    if (!this.isGeoambiente) {
      this.filters.organizationId = resolvedOrganizationId ?? null;
    }

    const { data: response, error } = await this.orpc.users.list({
      searchText: this.filters.searchText || undefined,
      role: this.filters.role ? this.filters.role : undefined,
      status: this.filters.status ? this.filters.status : undefined,
      organizationId: resolvedOrganizationId ?? undefined,
      limit: this.pageSize,
      cursor: this.pageIndex * this.pageSize,
      orderBy: this.buildOrderBy(),
    });
    if (!error) {
      this.dataSource.data = response.items.map((user) =>
        this.mapToTableUser(user),
      );
      this.totalItems = response.total;
    }
  }

  private buildOrderBy(): Record<string, 'asc' | 'desc'> | undefined {
    if (!this.sortActive || !this.sortDirection) {
      return undefined;
    }

    const sortableColumns: Record<string, string> = {
      name: 'name',
      email: 'email',
      role: 'role',
      status: 'status',
      lastAccess: 'lastAccess',
    };

    const column = sortableColumns[this.sortActive];
    if (!column) {
      return undefined;
    }

    return {
      [column]: this.sortDirection,
    };
  }

  private mapToTableUser(user: UserResponseDto): UserTableRow {
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      roleLabel: ROLE_LABELS[user.role],
      organizationId: user.organizationId ?? null,
      organizationName: user.organizationName,
      lastAccess: user.lastAccess ? new Date(user.lastAccess) : null,
      status: user.status,
    };
  }

  private buildRoleOptions(role: UserRole): SelectOption<UserRole>[] {
    const availableRoles = this.isGeoambiente
      ? Object.values(UserRole)
      : CLIENT_AVAILABLE_ROLES;

    return availableRoles.map((item) => ({
      value: item,
      label: ROLE_LABELS[item],
    }));
  }

  private canEditUser(user: UserTableRow): boolean {
    if (!this.canManageUsers) {
      return false;
    }

    if (this.isGeoambiente) {
      return true;
    }

    const organizationId = this.authenticatedUser?.organizationId;
    if (!organizationId) {
      return false;
    }

    return user.organizationId === organizationId;
  }

  private replaceUserInTable(updated: UserResponseDto): void {
    const mapped = this.mapToTableUser(updated);
    this.dataSource.data = this.dataSource.data.map((current) =>
      current.id === mapped.id ? mapped : current,
    );
  }
}
